/**
 * Bitcoin raw hex transaction deserializer.
 *
 * Parses raw serialized Bitcoin transactions into txViz's BitcoinTransactionInput type.
 *
 * Supports:
 * - Legacy transactions (version + inputs + outputs + locktime)
 * - SegWit transactions (BIP-141, with marker/flag/witness)
 * - PSBT (BIP-174, Partially Signed Bitcoin Transactions) — hex or base64 encoded
 *
 * The legacy-format serialization is preserved in the `rawHex` field
 * for correct TXID computation (TXID excludes witness data).
 */

import type { BitcoinTransactionInput, BitcoinInput, BitcoinOutput } from '@txviz/core';
import { hexToBytes, bytesToHex } from '@txviz/core';
import { serializeBitcoinTransaction } from './serializer.js';

/** PSBT magic bytes: "psbt" + 0xff separator */
const PSBT_MAGIC = new Uint8Array([0x70, 0x73, 0x62, 0x74, 0xff]);

/**
 * Parse a raw Bitcoin transaction or PSBT.
 *
 * Accepts:
 * - Raw transaction hex (0x-prefixed or bare)
 * - PSBT hex (0x-prefixed or bare, starts with 70736274ff)
 * - Base64-encoded PSBT (common format for PSBT sharing)
 *
 * @param input - The serialized transaction or PSBT
 * @returns A structured BitcoinTransactionInput with rawHex set
 * @throws {Error} If the input cannot be parsed
 */
export function parseRawBitcoinHex(input: string): BitcoinTransactionInput {
  const trimmed = input.trim();
  const bytes = decodeInput(trimmed);

  // Check for PSBT magic bytes
  if (isPSBT(bytes)) {
    return parsePSBT(bytes);
  }

  // Otherwise parse as raw transaction
  return parseRawTransaction(bytes);
}

/**
 * Decode the input string to bytes, handling hex, 0x-prefixed hex, and base64.
 */
function decodeInput(input: string): Uint8Array {
  // 0x-prefixed hex
  if (input.startsWith('0x') || input.startsWith('0X')) {
    return hexToBytes(input);
  }

  // All hex chars, even length → bare hex
  if (/^[0-9a-fA-F]+$/.test(input) && input.length % 2 === 0) {
    return hexToBytes(`0x${input}`);
  }

  // Base64 (commonly used for PSBTs)
  if (/^[A-Za-z0-9+/]+=*$/.test(input)) {
    try {
      const binaryStr = atob(input);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      return bytes;
    } catch {
      // Not valid base64, fall through
    }
  }

  throw new Error(
    'Could not decode Bitcoin input. Expected raw transaction hex, PSBT hex, or base64-encoded PSBT.',
  );
}

/**
 * Check if bytes start with the PSBT magic header.
 */
function isPSBT(bytes: Uint8Array): boolean {
  if (bytes.length < 5) return false;
  for (let i = 0; i < 5; i++) {
    if (bytes[i] !== PSBT_MAGIC[i]) return false;
  }
  return true;
}

// ── PSBT Parser (BIP-174) ─────────────────────────────────────

/**
 * Parse a PSBT and extract the unsigned transaction + UTXO data.
 *
 * PSBT format:
 *   [magic: 5 bytes] [global_map] [input_maps...] [output_maps...]
 *
 * Each map is a sequence of key-value pairs terminated by 0x00:
 *   [keyLen: varint] [keyType + keyData] [valueLen: varint] [valueData] ... [0x00]
 *
 * We extract:
 *   - Global key 0x00: the unsigned transaction bytes
 *   - Input key 0x01: witness UTXO (value + scriptPubKey) for each input
 */
function parsePSBT(bytes: Uint8Array): BitcoinTransactionInput {
  const reader = new BufferReader(bytes);

  // Skip magic bytes
  reader.skip(5);

  // Parse global map — extract unsigned transaction
  let unsignedTxBytes: Uint8Array | null = null;
  while (true) {
    const keyLen = reader.readVarInt();
    if (keyLen === 0) break; // Map terminator

    const keyType = reader.readUint8();
    const keyData = reader.readBytes(keyLen - 1); // remaining key bytes
    const valueLen = reader.readVarInt();
    const valueData = reader.readBytes(valueLen);

    if (keyType === 0x00 && keyData.length === 0) {
      // PSBT_GLOBAL_UNSIGNED_TX
      unsignedTxBytes = valueData;
    }
  }

  if (!unsignedTxBytes) {
    throw new Error('PSBT is missing the unsigned transaction (global key 0x00).');
  }

  // Parse the unsigned transaction
  const tx = parseRawTransaction(unsignedTxBytes);

  // Parse input maps — extract witness UTXO data for input values
  const inputValues: (bigint | undefined)[] = [];
  for (let i = 0; i < tx.inputs.length; i++) {
    let witnessUtxoValue: bigint | undefined;

    while (reader.remaining > 0) {
      const keyLen = reader.readVarInt();
      if (keyLen === 0) break; // Map terminator

      const keyType = reader.readUint8();
      reader.readBytes(keyLen - 1); // skip remaining key data
      const valueLen = reader.readVarInt();
      const valueData = reader.readBytes(valueLen);

      if (keyType === 0x01 && valueData.length >= 8) {
        // PSBT_IN_WITNESS_UTXO: [value: 8 bytes LE] [scriptPubKey]
        const view = new DataView(valueData.buffer, valueData.byteOffset, valueData.byteLength);
        witnessUtxoValue = view.getBigUint64(0, true);
      }
    }

    inputValues.push(witnessUtxoValue);
  }

  // Enrich inputs with UTXO values
  const enrichedInputs = tx.inputs.map((input, i) => {
    const value = inputValues[i];
    if (value !== undefined) {
      return { ...input, value };
    }
    return input;
  });

  // Parse output maps (skip through them — we don't need the data)
  for (let i = 0; i < tx.outputs.length; i++) {
    while (reader.remaining > 0) {
      const keyLen = reader.readVarInt();
      if (keyLen === 0) break;
      reader.readUint8(); // skip key type
      reader.readBytes(keyLen - 1); // skip remaining key data
      const valueLen = reader.readVarInt();
      reader.skip(valueLen);
    }
  }

  return {
    ...tx,
    inputs: enrichedInputs,
  };
}

// ── Raw Transaction Parser ─────────────────────────────────────

/**
 * Parse a raw serialized Bitcoin transaction (not PSBT).
 */
function parseRawTransaction(bytes: Uint8Array): BitcoinTransactionInput {
  const reader = new BufferReader(bytes);

  // 1. Version (4 bytes LE)
  const version = reader.readUint32LE();

  // 2. Detect SegWit: peek at next 2 bytes for marker (0x00) + flag (0x01)
  let isSegwit = false;
  const peeked = reader.peek(2);
  if (peeked[0] === 0x00 && peeked[1] === 0x01) {
    isSegwit = true;
    reader.skip(2); // consume marker + flag
  }

  // 3. Read inputs
  const inputCount = reader.readVarInt();
  const inputs: BitcoinInput[] = [];

  for (let i = 0; i < inputCount; i++) {
    // Previous txid (32 bytes in internal byte order — reverse for display)
    const prevTxidBytes = reader.readBytes(32);
    const txid = reverseHex(toHexNaked(prevTxidBytes));

    // Previous output index (4 bytes LE)
    const vout = reader.readUint32LE();

    // ScriptSig (varint-prefixed length + data)
    const scriptSigLen = reader.readVarInt();
    const scriptSigBytes = reader.readBytes(scriptSigLen);
    const scriptSig = toHexNaked(scriptSigBytes);

    // Sequence (4 bytes LE)
    const sequence = reader.readUint32LE();

    inputs.push({ txid, vout, scriptSig, sequence });
  }

  // 4. Read outputs
  const outputCount = reader.readVarInt();
  const outputs: BitcoinOutput[] = [];

  for (let i = 0; i < outputCount; i++) {
    // Value (8 bytes LE → bigint)
    const value = reader.readUint64LE();

    // ScriptPubKey (varint-prefixed length + data)
    const scriptLen = reader.readVarInt();
    const scriptBytes = reader.readBytes(scriptLen);
    const scriptPubKey = toHexNaked(scriptBytes);

    outputs.push({ value, scriptPubKey });
  }

  // 5. Read witness data (if SegWit)
  if (isSegwit) {
    for (let i = 0; i < inputCount; i++) {
      const witnessCount = reader.readVarInt();
      const witness: string[] = [];

      for (let j = 0; j < witnessCount; j++) {
        const itemLen = reader.readVarInt();
        const itemBytes = reader.readBytes(itemLen);
        witness.push(toHexNaked(itemBytes));
      }

      // Add witness data to the corresponding input
      inputs[i] = { ...inputs[i]!, witness };
    }
  }

  // 6. Locktime (4 bytes LE)
  const locktime = reader.readUint32LE();

  // Build the transaction without rawHex first, then serialize to get
  // the legacy (non-witness) format for TXID computation.
  const txWithoutRawHex: BitcoinTransactionInput = {
    version,
    inputs,
    outputs,
    locktime,
  };

  // The legacy serialization is what gets double-SHA256'd for the TXID.
  // serializeBitcoinTransaction() without rawHex produces the legacy format.
  const legacySerialized = serializeBitcoinTransaction(txWithoutRawHex);
  const rawHex = bytesToHex(legacySerialized).slice(2); // strip 0x prefix

  return {
    ...txWithoutRawHex,
    rawHex,
  };
}

// ── BufferReader ───────────────────────────────────────────────────

/**
 * Sequential binary reader with cursor tracking.
 * All reads advance the internal offset.
 */
class BufferReader {
  private readonly bytes: Uint8Array;
  private readonly view: DataView;
  private offset: number;

  constructor(bytes: Uint8Array) {
    this.bytes = bytes;
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    this.offset = 0;
  }

  readUint8(): number {
    const val = this.bytes[this.offset];
    if (val === undefined) throw new Error('Unexpected end of data');
    this.offset += 1;
    return val;
  }

  readUint32LE(): number {
    if (this.offset + 4 > this.bytes.length) throw new Error('Unexpected end of data');
    const val = this.view.getUint32(this.offset, true);
    this.offset += 4;
    return val;
  }

  readUint64LE(): bigint {
    if (this.offset + 8 > this.bytes.length) throw new Error('Unexpected end of data');
    const val = this.view.getBigUint64(this.offset, true);
    this.offset += 8;
    return val;
  }

  readBytes(count: number): Uint8Array {
    if (this.offset + count > this.bytes.length) {
      throw new Error(`Unexpected end of data: need ${count} bytes, have ${this.bytes.length - this.offset}`);
    }
    const slice = this.bytes.slice(this.offset, this.offset + count);
    this.offset += count;
    return slice;
  }

  readVarInt(): number {
    const first = this.readUint8();
    if (first < 0xfd) return first;
    if (first === 0xfd) {
      if (this.offset + 2 > this.bytes.length) throw new Error('Unexpected end of data');
      const val = this.view.getUint16(this.offset, true);
      this.offset += 2;
      return val;
    }
    if (first === 0xfe) {
      if (this.offset + 4 > this.bytes.length) throw new Error('Unexpected end of data');
      const val = this.view.getUint32(this.offset, true);
      this.offset += 4;
      return val;
    }
    // 0xff
    if (this.offset + 8 > this.bytes.length) throw new Error('Unexpected end of data');
    const val = Number(this.view.getBigUint64(this.offset, true));
    this.offset += 8;
    return val;
  }

  peek(count: number): Uint8Array {
    return this.bytes.slice(this.offset, this.offset + count);
  }

  skip(count: number): void {
    this.offset += count;
  }

  get remaining(): number {
    return this.bytes.length - this.offset;
  }
}

// ── Helpers ────────────────────────────────────────────────────────

/** Convert Uint8Array to hex string without 0x prefix. */
function toHexNaked(bytes: Uint8Array): string {
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += (bytes[i] ?? 0).toString(16).padStart(2, '0');
  }
  return hex;
}

/** Reverse a hex string (swap byte order). For txid display order. */
function reverseHex(hex: string): string {
  const pairs: string[] = [];
  for (let i = hex.length - 2; i >= 0; i -= 2) {
    pairs.push(hex.substring(i, i + 2));
  }
  return pairs.join('');
}
